import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { URL_CUSTOMERS } from '../service/endpoints';
import { deleteData, getData, patchData, postData } from '../service/APIClient';
import { getLocalStorage, setLocalStorage } from '../Helper/helperFunctions';

// Fetch customers
export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (_, thunkAPI) => {
    let customers = [];
    let currentPage = 1;
    let totalPages = 1;

    try {
      while (currentPage <= totalPages) {
        const response = await getData(`${URL_CUSTOMERS}?page=${currentPage}`);
        customers = [...customers, ...response.data];
        totalPages = response?.totalPages;
        currentPage++;
      }

      return customers.map(customer => ({
        key: customer?._id?.toString() || "",
        name: customer?.name || "",
        address: customer?.billingInfo?.address || "",
        paymentMethod: customer?.billingInfo?.paymentMethod || "",
        contact: customer?.contact?.name || "",
        email: customer?.contact?.email || "",
        phone: customer?.contact?.phone || "",
        sepiooAccount: customer?.settings?.sepiooAccount || "",
        sepiooLocation: customer?.settings?.sepiooLocation || "",
        sepiooApiKey: customer?.settings?.sepiooApiKey || "",
        createdAt: customer?.createdAt ? new Date(customer.createdAt).toISOString().split('T')[0] : "",
        updatedAt: customer?.updatedAt ? new Date(customer.updatedAt).toISOString().split('T')[0] : ""
      }));
    } catch (error) {
      return thunkAPI.rejectWithValue('Failed to fetch customers');
    }
  }
);

// Add customer
export const addCustomerAsync = createAsyncThunk(
  'customers/addCustomer',
  async (newCustomer, thunkAPI) => {
    const { ...rest } = newCustomer;
    newCustomer = {
      name: rest.name,
      billingInfo: {
        address: rest.billingAddress,
        paymentMethod: rest.paymentMethod
      },
      contact: {
        name: rest.contact,
        email: rest.email,
        phone: rest.phone
      },
      settings: {
        sepiooAccount: rest.sepiooAccount,
        sepiooLocation: rest.sepiooLocation,
        sepiooApiKey: rest.sepiooApiKey
      }
    };
    try {
      return await postData(URL_CUSTOMERS, newCustomer); // Assumes the response contains the full customer object
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update customer
export const updateCustomerAsync = createAsyncThunk(
  'customers/updateCustomer',
  async (updatedCustomer, thunkAPI) => {
    const { key, ...rest } = updatedCustomer; // Assuming `key` is the customer ID
    const customerUpdate = {
      name: rest.name,
      billingInfo: {
        address: rest.address,
        paymentMethod: rest.paymentMethod
      },
      contact: {
        name: rest.contact,
        email: rest.email,
        phone: rest.phone
      },
      settings: {
        sepiooAccount: rest.sepiooAccount,
        sepiooLocation: rest.sepiooLocation,
        sepiooApiKey: rest.sepiooApiKey
      }
    };
    try {
      return await patchData(`${URL_CUSTOMERS}/${key}`, customerUpdate);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

//Delete customer
export const deleteCustomerAsync = createAsyncThunk(
  'customers/deleteCustomer',
  async (customerId, thunkAPI) => {
    try {
      await deleteData(`${URL_CUSTOMERS}/${customerId}`);
      return customerId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

const customersSlice = createSlice({
  name: 'customers',
  initialState: {
    data: [],
    loading: false,
    addStatus: 'idle',
    status: 'idle',
    updateStatus: 'idle',
    error: null,
    selectedCustomer: getLocalStorage("tenantId") || null
  },
  reducers: {
    resetAddStatus: state => {
      state.addStatus = 'idle';
    },
    setSelectedCustomer: (state, action) => {
      setLocalStorage("tenantId", action.payload)
      state.selectedCustomer = action.payload; // Update the selected customer
    },
  },
  extraReducers: builder => {
    builder
      // Fetch customers
      .addCase(fetchCustomers.pending, state => {
        state.loading = true;
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.status = 'succeeded';
        state.data = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      // Add customer
      .addCase(addCustomerAsync.pending, state => {
        state.addStatus = 'loading';
      })
      .addCase(addCustomerAsync.fulfilled, (state, action) => {
        state.addStatus = 'succeeded';
        state.data.push(action.payload); // Add the new customer to the state
      })
      .addCase(addCustomerAsync.rejected, (state, action) => {
        state.addStatus = 'failed';
        state.error = action.payload || action.error.message;
      })
      // Update customer
      .addCase(updateCustomerAsync.pending, state => {
        state.updateStatus = 'loading';
      })
      .addCase(updateCustomerAsync.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        const index = state.data.findIndex(
          customer => customer.key === action.payload._id
        );
        if (index !== -1) {
          state.data[index] = { ...state.data[index], ...action.payload };
        }
      })
      .addCase(updateCustomerAsync.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.error = action.payload || action.error.message;
      })
      // Delete customer
      .addCase(deleteCustomerAsync.pending, state => {
        state.deleteStatus = 'loading';
      })
      .addCase(deleteCustomerAsync.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.data = state.data.filter(
          customer => customer.key !== action.payload
        );
      })
      .addCase(deleteCustomerAsync.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.error = action.payload || action.error.message;
      });
  }
});

export const { resetAddStatus, setSelectedCustomer  } = customersSlice.actions;
export default customersSlice.reducer;
